import { Card } from "@rothoblaas/design-system";
import type { ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import useI18n from "../../../../../hooks/useTranslations";
import styles from "../../LockCalculator.module.css";
import { formatCoefficientOrDash } from "../../model/coefficientFormat";
import {
  effectiveGammaMAluminium,
  type CalculationCoefficients,
  type LockCalculatorFormValues,
} from "../../model/form";
import { CoefficientsFields } from "./CoefficientsFields";
import { EnvironmentForm } from "./EnvironmentForm";
import { NoticeFormField as FormField } from "../notices/NoticeFormField";

type Props = {
  readOnly: boolean;
  onOpenHelp: (text: string, image?: string) => void;
  coefficients: CalculationCoefficients;
  stepper: ReactNode;
};

export const EnvironmentStep = ({ readOnly, onOpenHelp, coefficients, stepper }: Props) => {
  const { translation: t } = useI18n();
  const { control } = useFormContext<LockCalculatorFormValues>();
  const customCoefficients = useWatch({ control, name: "environment.customCoefficients" });
  return (
    <>
      <Card
        style={{ minHeight: 400 }}
        accentColor="var(--accent-base, var(--red))"
        shadow
        className={`${styles.span12} ${styles.compactCard} ${styles.stepCard}`}
      >
        {stepper}
        <Card.Title>{t.lockCalculatorModule.env.title}</Card.Title>
        <div className={styles.compactFormStack}>
          <EnvironmentForm readOnly={readOnly} onOpenHelp={onOpenHelp} coefficients={coefficients} />
          {/* One field for the section: it is what "go to" flashes. Custom shows the user's own values, which
              replace the normative ones for this calculation; otherwise the backend resolves them on save. */}
          <FormField name="environment.coefficients" label={t.lockCalculatorModule.env.coefficients} isOptional>
            {customCoefficients ? (
              <CoefficientsFields readOnly={readOnly} coefficients={coefficients} />
            ) : (
              <div className={styles.metricList}>
                <div>
                  <span>{t.lockCalculatorModule.env.kmod}</span>
                  <strong>{formatCoefficientOrDash(coefficients.kmod)}</strong>
                </div>
                <div>
                  <span>{t.lockCalculatorModule.env.gammaM}</span>
                  <strong>{formatCoefficientOrDash(coefficients.gammaM)}</strong>
                </div>
                <div>
                  <span>{t.lockCalculatorModule.env.gammaMAluminium}</span>
                  <strong>{formatCoefficientOrDash(effectiveGammaMAluminium(coefficients))}</strong>
                </div>
              </div>
            )}
          </FormField>
        </div>
      </Card>
    </>
  );
};
